
import { MutableRefObject, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts } from "../../../Styles";
import { DataObj, ListData_ValueTypes, QuickEntry, quickEntryChange, SelectFieldOption_Values, TableStyles_Types, ValueSteps, ValueType } from "../../../Definitions";
import { UndefinedOptions, useFeedcurveOptions, useGroupOptions, useSectionOptions } from "../../_Components";

import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, noop, Row, useReactTable } from "@tanstack/react-table";

// Data
import { useAppSelector } from "../../../Store";
import { MaintainAnimal } from "../../../Services";
import { FilteredAnimalsResponse, GET_FILTERED_ANIMALS } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getFilteredAnimalsCall";
import { useQuery } from "@apollo/client";
import { keyBy, merge } from "lodash";
import { GenericDataParameters, GenericRow, useColumnConstructor } from "../../Tables/_Tables/useColumnConstructor";
import { ReactiveTable } from "../../Tables";
import { selectAllFeedCurveMilestoneOptions } from "../../../Store/OptionsHandlers/optionsCombinedReducer";


type QuickEntryModalLvl3Props = {
  quickEntryData: QuickEntry[];
  quickEntryChange: quickEntryChange[];
  dirtyDataRef: MutableRefObject<MaintainAnimal[]>
  focus_view?: boolean
};
interface TableRow extends GenericRow {
  index: number;
  animalNo: number;
  animalGroup?: { label: string; id: number };
  animalLatestMilestone?: { label: string; id: number };
  animalFeedcurve?: SelectFieldOption_Values;
  animalLocation?: { label: string; id: number };
  animalMilestoneDay?: number;
};

const camelToSnakeCase = (stringValue: string) => {
  // the table id for feed curve is with misspelled feedcurve in the database
  return stringValue === "feedCurveId" ? "feedcurve_id" : stringValue.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

export default function QuickEntryModalLvl3({ quickEntryData, quickEntryChange, dirtyDataRef, focus_view }: QuickEntryModalLvl3Props) {
  const { t } = useTranslation();
  const [editTable] = useState(true)

  const groupOptions = useGroupOptions(UndefinedOptions.nan);
  const { feedCurveOptions } = useFeedcurveOptions(UndefinedOptions.nan);
  const { locationOptions } = useSectionOptions(UndefinedOptions.nan)

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";

  const filters = quickEntryData.map((filter) => ({
    ids: filter.value.map(({ id }) => id),
    key: camelToSnakeCase(filter.key),
  }));

  const { loading, data } = useQuery<FilteredAnimalsResponse>(
    GET_FILTERED_ANIMALS,
    { variables: { domain: farm, filters } }
  );


  const feedCurveMilestones = useAppSelector(selectAllFeedCurveMilestoneOptions).reduce((agg, milestone) => {
    const { feedCurveId } = milestone;
    agg[feedCurveId] = agg[feedCurveId] || []
    agg[feedCurveId].push(milestone)
    return agg;
  }, {} as { [key: number]: (ValueType & { feedCurveId: number; index: number; milestoneId: number; })[] });


  const tableData = useMemo(() => {
    if (!data?.animal.filtered) return [];

    return data.animal.filtered
      .map((filteredAnimal, index): TableRow | null => {

        const animalGroupChange = quickEntryChange.find(q => q.key === "animal_group_id");
        const milestoneChange = quickEntryChange.find(q => q.key === "milestone_id");
        const locationChange = quickEntryChange.find(q => q.key === "location_id");
        const feedCurveChange = quickEntryChange.find(q => q.key === "feedCurveId");
        const milestoneDayChange = quickEntryChange.find(q => q.key === "milestone_day" as unknown as DataObj);
        const feedCurveSelectedOption = feedCurveOptions.find(({ id }) => (id === (feedCurveChange?.value[0].id ?? filteredAnimal.feedcurve_id)));
        const resetConsumptionChange = quickEntryChange.find(q => q.key === "reset_consumption" as unknown as DataObj);
        const correctionChange = quickEntryChange.find(q => q.key === "correction" as unknown as DataObj);

        const milestoneOptions = feedCurveSelectedOption ? feedCurveMilestones[feedCurveSelectedOption.id] : [];


        const milestone = milestoneOptions.find((option) => (
          option.milestoneId === milestoneChange?.value[0].id
        ))

        const changedData: MaintainAnimal = { animal_id: filteredAnimal.animal_id };

        if (animalGroupChange) {
          changedData.animal_group_id = animalGroupChange.value[0].id
        }
        if (milestone && milestone.id !== filteredAnimal.fk_feedcurve_milestones) {
          changedData.fk_feedcurve_milestones = milestone.id;
        }
        if (feedCurveChange) {
          changedData.feedcurve_id = feedCurveChange.value[0].id
        }
        if (locationChange) {
          changedData.location = locationChange.value[0].id
        }
        if (milestoneDayChange) {
          const value = milestoneDayChange.value[0].id
          changedData.milestone_day = Math.round((Date.now() - Number(value) * 86400000) / 1000);
        }
        if (resetConsumptionChange) {
          changedData.reset_consumption = resetConsumptionChange.value
        }
        if (correctionChange) {
          changedData.correction = correctionChange.value[0].id
        }
        dirtyDataRef.current = Object.values(merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([changedData], "animal_id")
        ));

        const _groupOptions = groupOptions.filter(option => option.id !== undefined) as { label: string; id: number }[]
        const _locationOptions = locationOptions.filter(option => option.id !== undefined) as { label: string; id: number }[]

        return {
          id: filteredAnimal.animal_id,
          animalNo: filteredAnimal.animal_number,
          animalGroup: _groupOptions.find(({ id }) => id === animalGroupChange?.value[0].id || ((Number.isNaN(id) && Number.isNaN(animalGroupChange?.value[0].id)))),
          animalLatestMilestone: milestone ? milestone : milestoneOptions[0],
          animalFeedcurve: feedCurveSelectedOption ? feedCurveSelectedOption : { id: NaN, label: "no feed curve" },
          animalLocation: _locationOptions.find(({ id }) => id === locationChange?.value[0].id || ((Number.isNaN(id) && Number.isNaN(locationChange?.value[0].id)))),
          animalMilestoneDay: milestoneDayChange?.value[0].id,
          animalResetConsumption: resetConsumptionChange?.value,
          animalCorrection: correctionChange?.value[0].id,
          index,
        };
      })
      .filter((row): row is TableRow => row !== null);
  }, [data?.animal.filtered, quickEntryChange, dirtyDataRef, groupOptions, locationOptions]);

  const accessorData = useMemo<GenericDataParameters<TableRow>[]>(() => {
    const rows: GenericDataParameters<TableRow>[] = [
      { headerValue: `${t("Animal No.", { defaultValue: "Animal No." })}`, accessor: "animalNo", type: ListData_ValueTypes.String, focus_view: focus_view }
    ];
    if (quickEntryChange.some(f => f.key === "milestone_day" as unknown as DataObj)) {
      rows.push({ headerValue: t("params.milestoneDay", { defaultValue: "milestone Day" }), accessor: "animalMilestoneDay", type: ListData_ValueTypes.Number, focus_view: focus_view });
    }
    if (quickEntryChange.some(f => f.key === "animal_group_id")) {
      rows.push({ headerValue: t("params.group", { defaultValue: "group" }), accessor: "animalGroup", type: ListData_ValueTypes.List, options: groupOptions, focus_view: focus_view });
    }
    if (quickEntryChange.some(f => f.key === "location_id")) {
      rows.push({ headerValue: t("params.location", { defaultValue: "location" }), accessor: "animalLocation", type: ListData_ValueTypes.List, options: locationOptions, focus_view: focus_view });
    }
    if (quickEntryChange.some(f => f.key === "milestone_id")) {
      rows.push({
        headerValue: t("params.milestone", { defaultValue: "milestone" }), accessor: "animalLatestMilestone", focus_view: focus_view, type: ListData_ValueTypes.List, options: (row?: Row<TableRow>) => {
          if (!row) {
            return [];
          }
          return (row.original.animalFeedcurve?.id ? feedCurveMilestones[Number(row.original.animalFeedcurve.id)] : []);
        },
      });
    }

    if (quickEntryChange.some(f => f.key === "feedCurveId")) {
      rows.push({ headerValue: t("params.feedCurve", { defaultValue: "feed curve" }), accessor: "animalFeedcurve", type: ListData_ValueTypes.List, options: feedCurveOptions, focus_view: focus_view });
    }

    if (quickEntryChange.some(f => f.key === "reset_consumption" as unknown as DataObj)) {
      rows.push({ headerValue: t("params.reset_consumption", { defaultValue: "reset consumption" }), accessor: "animalResetConsumption", type: ListData_ValueTypes.Check, focus_view: focus_view });
    }

    if (quickEntryChange.some(f => f.key === "correction" as unknown as DataObj)) {
      rows.push({ headerValue: t("feed.feedCorrection.feedCorrection", { defaultValue: "Feed Correction" }), accessor: "animalCorrection", type: ListData_ValueTypes.Number, focus_view: focus_view, maxValueNumber: 100, minValueNumber: (row) => -99, valueStep: ValueSteps.integers });
    }

    return rows;
  }, [t, focus_view, quickEntryChange, groupOptions, locationOptions, feedCurveOptions, feedCurveMilestones]);

  const columns = useColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: focus_view ? TableStyles_Types.primaryTable : TableStyles_Types.blueTable,
    canEdit: editTable
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning: {
        left: ["animalNo"]
      }
    },
    meta: {
      deleteRow: noop,
      updateData: (rowId, columnId, value, row) => {
        if (columnId === "animalGroup") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              animal_group_id: selectValue.id
            }], "animal_id")
            )
          );
          return;
        }
        if (columnId === "animalLocation") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              location: selectValue.id
            }], "animal_id")
            )
          );
          return;
        }
        if (columnId === "animalMilestoneDay") {
          const newValue = Date.now() - Number(value) * 86400000;
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              milestone_day: Math.round(newValue / 1000)
            }], "animal_id")
            )
          );
          return;
        }
        if (columnId === "animalFeedcurve") {
          const selectValue = value as any as SelectFieldOption_Values;
          const milestoneOptionsForFeedCurve = selectValue?.id ? feedCurveMilestones[Number(selectValue.id)] : [];
          const milestone = milestoneOptionsForFeedCurve.find((option) => {
            return row.original.animalLatestMilestone?.id === option.id
          }) ?? milestoneOptionsForFeedCurve[0];

          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              feedcurve_id: selectValue.id,
              fk_feedcurve_milestones: milestone.id ?? undefined
            }], "animal_id"))
          );
          return;
        }
        if (columnId === "animalLatestMilestone") {
          const selectValue = value as any as SelectFieldOption_Values;
          if (row.original.animalFeedcurve?.id) {
            if (selectValue?.id) {
              dirtyDataRef.current = Object.values(
                merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
                  animal_id: rowId,
                  fk_feedcurve_milestones: Number(selectValue.id)
                }], "animal_id"))
              );
            }
          }
          return;
        }
        if (columnId === "animalResetConsumption") {
          const isChecked = !!value;

          const existing = keyBy(dirtyDataRef.current, "animal_id");

          const currentRow = existing[rowId] || { animal_id: rowId };

          if (isChecked) {
            currentRow.reset_consumption = Math.floor(Date.now() / 1000);
          } else {
            delete currentRow.reset_consumption;
          }

          dirtyDataRef.current = Object.values({
            ...existing,
            [rowId]: currentRow,
          });

          return;
        }
        if (columnId === "animalCorrection") {
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animal_id"), keyBy([{
              animal_id: rowId,
              correction: Number(value)
            }], "animal_id")
            )
          );
          return;
        }
      }
    }
  })

  const reactiveTable = useMemo(() => (
    <ReactiveTable
      table={table}
      tableType={focus_view ? TableStyles_Types.primaryTable : TableStyles_Types.blueTable}
      emptyText={""}
      renderFooter={false}
    />
  ), [focus_view, table]);

  if (loading) {
    return (<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <p style={{ ...fonts.paragraph, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4), color: colors.greyLightTertiary }}>Loading…</p>
    </div>);
  }

  return (
    <div style={{ height: "100%", position: "relative", backgroundColor: focus_view ? colors.greyLightSecondary : colors.blueDarkPrimary, borderRadius: focus_view ? "10px" : "0px", border: focus_view ? "5px solid " + colors.greyPrimary : "none", boxSizing: "border-box", overflow: "hidden" }}>
      {tableData.length === 0 ? <p style={{ ...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center" }}>{t("animalPages.animals.noAnimalsMatchTheSelection", { defaultValue: "No animals match the selection" })}</p> : reactiveTable}
      {focus_view ? <></> : <div style={{ width: "12px", height: "24px", backgroundColor: colors.blueDarkPrimary, position: "absolute", top: "0px", right: "0px" }} />}
    </div>
  );
}
